import type { ReactNode } from 'react';
import { createContext, useMemo } from 'react';

import type { LicenseCode } from '@/config/licenses';
import { usePermissions } from '@/contexts/auth';
import { isValidCageId, useGetCageContext } from '@/domain/cages';
import { isNotFoundError } from '@/domain/shared/errors';
import { hasLicense, hasLicenseByCode } from '@/utils/licenses';

import type { CageContextLicenseChecks, CageContextValue } from './types';

/**
 * Keeps the two closures referentially stable across the invalid-id, loading, error and no-data
 * branches, so consumers that memoise on them do not churn while the query resolves.
 */
const NO_LICENSES: CageContextLicenseChecks = {
    hasCageLicense: () => false,
    hasLicenseByCode: () => false,
};

export const CageContext = createContext<CageContextValue | undefined>(undefined);

CageContext.displayName = 'CageContext';

interface CageProviderProps {
    cageId: number;
    /**
     * The cohort the pages are about (`fg`). Only `dataAvailability` follows it — which data
     * exists is a question about the fish; the rest of the context describes the pen.
     */
    fishGroupId?: number;
    children: ReactNode;
}

export function CageProvider(props: CageProviderProps): ReactNode {
    const { cageId, fishGroupId, children } = props;
    const hasValidCageId = isValidCageId(cageId);

    const { data, isLoading, error, isFetching, refetch } = useGetCageContext(
        cageId,
        { fishGroupId },
        { enabled: hasValidCageId }
    );
    const { isOptoScaleAdmin } = usePermissions();

    const value = useMemo<CageContextValue>(() => {
        if (!hasValidCageId) {
            return {
                ...NO_LICENSES,
                loading: false,
                error: new Error('Invalid cage id'),
                notFound: true,
                cageId,
            };
        }

        if (isLoading) {
            return { ...NO_LICENSES, loading: true, notFound: false, cageId };
        }

        if (error) {
            return {
                ...NO_LICENSES,
                loading: false,
                error,
                notFound: isNotFoundError(error),
                cageId,
                retry: () => void refetch(),
                isRetrying: isFetching,
            };
        }

        if (!data) {
            return {
                ...NO_LICENSES,
                loading: false,
                error: new Error('No cage context data'),
                notFound: true,
                cageId,
            };
        }

        const { licenses } = data;

        return {
            loading: false,
            notFound: false,
            cageId,
            // Passed through by reference. `useGetCageContext` memoises the mapped payload, so
            // these identities are stable for as long as the query result is.
            cage: data.cage,
            location: data.location,
            client: data.client,
            stock: data.stock,
            dataAvailability: data.dataAvailability,
            bioscope: data.bioscope,
            // `hasLicense` / `hasLicenseByCode` are structurally typed on `{ code: string }[]`, so
            // the domain licence blocks go straight in.
            hasCageLicense: (...patterns: string[]) =>
                isOptoScaleAdmin || hasLicense(licenses, patterns),
            hasLicenseByCode: (licenseCode: LicenseCode) =>
                isOptoScaleAdmin || hasLicenseByCode(licenses, licenseCode),
        };
    }, [data, error, isLoading, isFetching, refetch, cageId, hasValidCageId, isOptoScaleAdmin]);

    return <CageContext value={value}>{children}</CageContext>;
}
